import React, { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";

import UserCertificates from "../pages/learn/UserCertificates";
import "../styles/UserProfile.css";
import { connectWisdomWallet, silentWisdomAccount, wisdomRpcUrl } from "../chain";
import { signedAuthHeaders } from "../api";
import { coverForBadge } from "../catalogCovers";

const UserProfile = () => {
    const [walletAddress, setWalletAddress] = useState("");
    const [balance, setBalance] = useState(null);
    const [connected, setConnected] = useState(false);
    const [profile, setProfile] = useState({
        name: "",
        nickname: "",
        bio: "",
        profilePic: "/pictures/default-avatar.png",
        badges: [],
        learningPlans: []
    });
    const [form, setForm] = useState({ name: "", nickname: "", bio: "" });
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState("");

    const fetchBalance = async (address) => {
        try {
            const provider = new ethers.JsonRpcProvider(wisdomRpcUrl());
            const balanceWei = await provider.getBalance(address);
            setBalance(ethers.formatEther(balanceWei));
        } catch (error) {
            console.error("Failed to fetch balance:", error);
        }
    };

    const loadProfile = useCallback(async (address) => {
        if (!address) {
            return;
        }
        try {
            const res = await fetch(`/api/profile/${encodeURIComponent(address)}`);
            if (!res.ok) {
                return;
            }
            const data = await res.json();
            setProfile((prev) => ({
                ...prev,
                name: data.name || "",
                nickname: data.nickname || "",
                bio: data.bio || "",
                profilePic: data.avatarUrl || "/pictures/default-avatar.png",
            }));
            setForm({
                name: data.name || "",
                nickname: data.nickname || "",
                bio: data.bio || "",
            });
        } catch (error) {
            console.error("Failed to load profile:", error);
        }
    }, []);

    const loadAchievements = useCallback(async (address) => {
        if (!address) {
            return;
        }
        try {
            const res = await fetch(`/api/achievements/${encodeURIComponent(address)}`);
            if (!res.ok) {
                return;
            }
            const data = await res.json();
            setProfile((prev) => ({
                ...prev,
                badges: Array.isArray(data.badges) ? data.badges : [],
                learningPlans: Array.isArray(data.learningPlans)
                    ? data.learningPlans.map((plan) => plan.title).filter(Boolean)
                    : [],
            }));
        } catch (error) {
            console.error("Failed to load achievements:", error);
        }
    }, []);

    const hydrateWallet = useCallback(async (address) => {
        if (!address) {
            return;
        }
        localStorage.setItem("user", address);
        setWalletAddress(address);
        setConnected(true);
        fetchBalance(address);
        loadProfile(address);
        loadAchievements(address);
    }, [loadAchievements, loadProfile]);

    useEffect(() => {
        // Prefer the actually-connected wallet. Never trust a bare localStorage
        // value on its own, or an old address gets shown while disconnected.
        silentWisdomAccount().then((address) => {
            if (address) {
                hydrateWallet(address);
            } else {
                localStorage.removeItem("user");
                setConnected(false);
                setWalletAddress("");
            }
        });
    }, [hydrateWallet]);

    const saveProfile = async (event) => {
        event.preventDefault();
        if (!walletAddress) {
            return;
        }
        setSaving(true);
        setSaveMessage("");
        try {
            const res = await fetch("/api/profile", {
                method: "PUT",
                headers: await signedAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || "Could not save profile");
            }
            setProfile((prev) => ({
                ...prev,
                name: data.name || "",
                nickname: data.nickname || "",
                bio: data.bio || "",
                profilePic: data.avatarUrl || prev.profilePic,
            }));
            setSaveMessage("Profile saved.");
        } catch (error) {
            setSaveMessage((error && error.message) || "Could not save profile.");
        } finally {
            setSaving(false);
        }
    };

    const uploadAvatar = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file || !walletAddress) {
            return;
        }
        setSaving(true);
        setSaveMessage("");
        try {
            const body = new FormData();
            body.append("image", file);
            const res = await fetch("/api/profile/avatar", {
                method: "POST",
                headers: await signedAuthHeaders(),
                body,
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || "Could not upload photo");
            }
            setProfile((prev) => ({
                ...prev,
                profilePic: data.avatarUrl || prev.profilePic,
            }));
            setSaveMessage("Photo updated.");
        } catch (error) {
            setSaveMessage((error && error.message) || "Could not upload photo.");
        } finally {
            setSaving(false);
            event.target.value = "";
        }
    };

    const connectWallet = async () => {
        try {
            const address = await connectWisdomWallet();
            await hydrateWallet(address);
        } catch (error) {
            console.error("Wallet connection failed:", error);
            alert((error && error.message) || "Wallet connection failed.");
        }
    };

    return (
        <div className="user-profile">
            <div className="profile-header">
                <img src={profile.profilePic} alt="Profile" className="profile-pic" />
                <h2>{profile.name || profile.nickname || "Guest User"}</h2>
                {profile.nickname ? <p className="nickname">@{profile.nickname}</p> : null}
                {connected ? (
                    <>
                        <p className="wallet-address">{walletAddress}</p>
                        <p><strong>WSD Balance:</strong> {balance} WSD</p>
                    </>
                ) : (
                    <>
                        <p className="wallet-address wallet-address--placeholder">(yourWallet)</p>
                        <button onClick={connectWallet} className="connect-btn">
                            Connect Wallet
                        </button>
                    </>
                )}
            </div>
            {connected ? (
                <form className="profile-form" onSubmit={saveProfile}>
                    <label>
                        Name
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            maxLength={120}
                            placeholder="Your name"
                        />
                    </label>
                    <label>
                        Nickname
                        <input
                            value={form.nickname}
                            onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                            maxLength={80}
                            placeholder="Nickname"
                        />
                    </label>
                    <label>
                        Bio
                        <textarea
                            value={form.bio}
                            onChange={(e) => setForm({ ...form, bio: e.target.value })}
                            maxLength={500}
                            placeholder="A short bio"
                            rows={3}
                        />
                    </label>
                    <label className="avatar-upload">
                        Photo / avatar
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadAvatar} />
                    </label>
                    <button type="submit" className="update-btn" disabled={saving}>
                        {saving ? "Saving…" : "Save profile"}
                    </button>
                    {saveMessage ? <p className="save-message">{saveMessage}</p> : null}
                </form>
            ) : null}
            <UserCertificates walletAddress={walletAddress} />
            
            <div className="badges-section">
                <h3>Achievements</h3>
                <div className="badges-container">
                    {profile.badges.length === 0 ? (
                        <p>No achievements yet.</p>
                    ) : profile.badges.map((badge, index) => (
                        <div key={badge.id || index} className="badge-tile">
                            <img src={coverForBadge(badge)} alt="" />
                            <span>{badge.title || badge}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="badges-section">
                <h3>Learning Plans</h3>
                <div className="badges-container">
                    {profile.learningPlans.length === 0 ? (
                        <p>No learning plans yet.</p>
                    ) : profile.learningPlans.map((plan, index) => (
                        <span key={index} className="badge">{plan}</span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
